import dayjs from "dayjs";

/** The 7 onboarding forms in review order, with the user-model status/comment fields. */
export var JOINEE_FORMS = [
  { id: "newJoinee", key: "isNewJoineeFormComplete", label: "New Joinee Form", comment: "newJoineeFormComment" },
  { id: "sdf", key: "isSelfDecalrationFormComplete", label: "Self Declaration Form (SDF)", comment: "selfDecalrationFormComment" },
  { id: "epf", key: "isEPFFormComplete", label: "EPF Form", comment: "EPFFormComment" },
  { id: "form11", key: "isForm11Complete", label: "Form 11", comment: "form11Comment" },
  { id: "formF", key: "isFormFComplete", label: "Form F", comment: "formFComment" },
  { id: "formQ", key: "isFormQComplete", label: "Form Q", comment: "FormQComment" },
  { id: "nda", key: "isNDAFormComplete", label: "NDA Form", comment: "NDAFormComment" },
];

export function findJoineeForm(id) {
  for (var i = 0; i < JOINEE_FORMS.length; i++) {
    if (JOINEE_FORMS[i].id === id) return JOINEE_FORMS[i];
  }
  return null;
}

/** Overall status cards; `iconColor` differs from `border` only where Figma says so. */
export var JOINEE_STATUS_META = {
  upcoming: { label: "Upcoming Joiner", icon: "User", bg: "#E7F1FF", color: "#4F7DFF", iconColor: "#4F7DFF" },
  pending: { label: "Pending", icon: "UserCircleDashed", bg: "#FFF0F1", color: "#FF5967", iconColor: "#FF5967" },
  inProgress: { label: "In Progress", icon: "SpinnerGap", bg: "#EEFFFB", color: "#00A49B", iconColor: "#00A49B" },
  resubmission: { label: "Resubmission", icon: "ArrowClockwise", bg: "#FFF9EC", color: "#FFAB2F", iconColor: "#FF8F07", chipColor: "#FF8F07" },
  review: { label: "Review", icon: "ListMagnifyingGlass", bg: "#F3F0FF", color: "#9F72FF", iconColor: "#833AFF", chipColor: "#833AFF" },
  completed: { label: "Completed", icon: "CheckSquare", bg: "#EAFFE4", color: "#09B500", iconColor: "#09B500" },
};

export var JOINEE_STAT_ORDER = ["upcoming", "pending", "inProgress", "resubmission", "review", "completed"];

/** Chip colours for an overall status (table + drawer). */
export function joineeStatusChipMeta(status) {
  var meta = JOINEE_STATUS_META[status] || JOINEE_STATUS_META.pending;
  return { label: meta.label, bg: meta.bg, color: meta.chipColor || meta.color };
}

/** One-line hint shown next to Approve / Re-submit on the review page. */
export function formStatusText(status, waitingOnHrData) {
  if (waitingOnHrData) return "Complete and save the HR section of this form to enable Approve and Re-submit.";
  if (status === "submitted" || status === "resubmitted") return "Submitted by the candidate and waiting for your review.";
  if (status === "review done") return "Approved. You can still request changes if needed.";
  if (status === "resubmission required") return "Changes requested. Waiting for the candidate to re-submit.";
  if (status === "draft") return "The candidate is still filling in this form.";
  return "The candidate has not started this form yet.";
}

function isSubmittedOrApproved(status) {
  return status === "submitted" || status === "resubmitted" || status === "review done";
}

/**
 * Checked in order: Completed → Resubmission → Review → In Progress → Pending.
 * A draft counts as touched.
 */
export function deriveJoineeStatus(record) {
  var statuses = JOINEE_FORMS.map(function (form) {
    return (record && record[form.key]) || "pending";
  });
  if (statuses.every(function (s) { return s === "review done"; })) return "completed";
  if (statuses.some(function (s) { return s === "resubmission required"; })) return "resubmission";
  if (statuses.every(isSubmittedOrApproved)) return "review";
  if (statuses.some(function (s) { return s !== "pending"; })) return "inProgress";
  return "pending";
}

export function toDayKey(value) {
  if (!value) return "";
  var d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
}

/** DOJ is today or later. `todayKey` is injectable for tests. */
export function isUpcomingJoinee(record, todayKey) {
  var doj = toDayKey(record && record.doj);
  return Boolean(doj) && doj >= (todayKey || dayjs().format("YYYY-MM-DD"));
}

/** Inclusive DOJ range; an open end means "no limit" on that side. */
export function isInDojRange(record, range) {
  if (!range || (!range.start && !range.end)) return true;
  var doj = toDayKey(record && record.doj);
  if (!doj) return false;
  if (range.start && doj < range.start) return false;
  if (range.end && doj > range.end) return false;
  return true;
}

export function matchesSearch(record, search) {
  var needle = String(search || "").trim().toLowerCase();
  if (!needle) return true;
  return ["name", "designation", "email"].some(function (field) {
    return String((record && record[field]) || "").toLowerCase().indexOf(needle) !== -1;
  });
}

/** Selected status tabs OR together; "upcoming" ANDs with them. */
export function matchesStatTabs(record, tabs, todayKey) {
  if (!tabs || !tabs.length) return true;
  if (tabs.indexOf("upcoming") !== -1 && !isUpcomingJoinee(record, todayKey)) return false;
  var statusTabs = tabs.filter(function (tab) {
    return tab !== "upcoming";
  });
  return !statusTabs.length || statusTabs.indexOf(record.status) !== -1;
}

/** Card counts follow the date range only, so they stay stable while searching. */
export function countJoineeStats(records, todayKey) {
  var counts = { upcoming: 0, pending: 0, inProgress: 0, resubmission: 0, review: 0, completed: 0 };
  (records || []).forEach(function (record) {
    if (isUpcomingJoinee(record, todayKey)) counts.upcoming += 1;
    if (counts[record.status] !== undefined) counts[record.status] += 1;
  });
  return counts;
}
